import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';
import { catchError, exhaustMap, map, switchMap } from 'rxjs/operators';
import {
  Dictionary,
  DomainSetting,
  Section,
  SectionDomainSetting,
} from '../../models';
import { AccountService } from '../../services/account.service';
import { DomainSettingsService } from '../../services/domain-settings.service';
import {
  accountDomainSettingActions,
  getSectionsActions,
} from '../actions/domain-settings-edit.actions';
import {
  deleteSettingsActions,
  getAccountSettingActions,
  getDomainSettingsAction,
  toggleAccountSettingActions,
} from '../actions/domain-settings.actions';
import { State } from '../reducers';

@Injectable()
export class DomainSettingsEffects {
  getDomainSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDomainSettingsAction.get),
      switchMap(() =>
        this.domainSettingsService.getSettings().pipe(
          map((settings) => getDomainSettingsAction.success({ settings })),
          catchError((err) => {
            this.toastService.error(
              `${this.getErrorMessage(err)}`,
              'Request failed',
            );
            return [getDomainSettingsAction.fail()];
          }),
        ),
      ),
    ),
  );

  toggleDomainSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleAccountSettingActions.toggle),
      exhaustMap(({ accountId, enabled }) =>
        this.domainSettingsService.toggle(accountId, enabled).pipe(
          map(() =>
            toggleAccountSettingActions.success({ accountId, enabled }),
          ),
          catchError((err) => {
            this.toastService.error(
              `${this.getErrorMessage(err)}`,
              'Request failed',
            );
            return [
              toggleAccountSettingActions.fail(),
              toggleAccountSettingActions.success({
                accountId,
                enabled: !enabled,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  accountDomainSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAccountSettingActions.get),
      exhaustMap(({ accountId }) =>
        forkJoin([
          this.accountService.getSections(accountId),
          this.domainSettingsService.getAccountSetting(accountId),
        ]).pipe(
          switchMap(([sections, setting]) => {
            const accountDomainSetting = this.toAccountDomainSetting(
              setting,
              sections,
            );
            return [
              getSectionsActions.success({ sections }),
              accountDomainSettingActions.addAccountDomainSetting({
                accountDomainSetting,
              }),
              getAccountSettingActions.success(),
            ];
          }),
          catchError(() => {
            this.toastService.error(
              `Could not fetch domain settings for account: ${accountId}`,
              'Request failed',
            );
            return [getAccountSettingActions.fail()];
          }),
        ),
      ),
    ),
  );

  deleteDomainSetting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteSettingsActions.delete),
      exhaustMap(({ accountId }) =>
        this.domainSettingsService.delete(accountId).pipe(
          map(() => deleteSettingsActions.success({ accountId })),
          catchError((err) => {
            this.toastService.error(
              `${this.getErrorMessage(err)}`,
              'Request failed',
            );
            return [deleteSettingsActions.fail()];
          }),
        ),
      ),
    ),
  );

  private getErrorMessage(err: HttpErrorResponse) {
    const { detail } = (err && err.error) || {};
    return detail || 'Something went wrong';
  }

  private toAccountDomainSetting(
    setting: DomainSetting,
    accountSections: Dictionary<Section>,
  ) {
    const { sections = {} } = setting.domainNames;
    const sectionDomainSetting: SectionDomainSetting[] = [];
    for (const [key, value] of Object.entries(sections)) {
      const { domainName } = value;
      sectionDomainSetting.push({
        id: key,
        name: accountSections[key].name,
        domainName,
      });
    }
    return {
      accountId: setting.accountId,
      enabled: setting.enabled,
      domainNames: {
        accountDomain: setting.domainNames.account || '',
        sections: sectionDomainSetting,
      },
    };
  }

  constructor(
    private readonly actions$: Actions,
    private readonly accountService: AccountService,
    private readonly domainSettingsService: DomainSettingsService,
    private readonly toastService: ToastrService,
    private readonly store: Store<State>,
  ) {}
}
